import React, { useState, useEffect } from 'react';
import { 
  Crown, Star, Home, Loader2, DollarSign, Users, Check, X,
  Edit2, Save, RefreshCw, AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAdmin } from '../../context/AdminContext';
import { toast } from 'sonner';
import { format } from 'date-fns';

const TIER_ICONS = {
  standard: Home,
  featured: Star,
  concierge: Crown
};

const TIER_COLORS = {
  standard: 'bg-slate-600',
  featured: 'bg-amber-500',
  concierge: 'bg-trust-blue'
};

const AdminSubscriptionsPage = () => {
  const { 
    getSubscriptionTiers, 
    getOwnerSubscriptions, 
    updateTierPrice, 
    toggleTier,
    changeUserSubscription,
    getSubscriptionStats 
  } = useAdmin();
  
  const [tiers, setTiers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTier, setEditingTier] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);
  const [changingUser, setChangingUser] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tiersData, subsData, statsData] = await Promise.all([
        getSubscriptionTiers(),
        getOwnerSubscriptions(),
        getSubscriptionStats()
      ]);
      setTiers(tiersData.tiers || []);
      setSubscriptions(subsData.subscriptions || []);
      setStats(statsData);
    } catch (error) {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPrice = (tier) => {
    setEditingTier(tier.tier);
    setEditPrice(tier.price.toString());
  };

  const handleSavePrice = async (tierName) => {
    const newPrice = parseFloat(editPrice);
    if (isNaN(newPrice) || newPrice <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    setSavingPrice(true);
    try {
      await updateTierPrice(tierName, newPrice);
      toast.success(`${tierName} price updated to $${newPrice}`);
      setEditingTier(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update price');
    } finally {
      setSavingPrice(false);
    }
  };

  const handleToggleTier = async (tierName, currentEnabled) => {
    try {
      await toggleTier(tierName, !currentEnabled);
      toast.success(`${tierName} tier ${!currentEnabled ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle tier');
    }
  };

  const handleChangeUserTier = async (userId, newTier) => {
    setChangingUser(userId);
    try {
      await changeUserSubscription(userId, newTier);
      toast.success('User subscription updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to change user subscription');
    } finally {
      setChangingUser(null);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-trust-blue" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="admin-subscriptions">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            Subscriptions
          </h1>
          <p className="text-slate-400">
            Manage subscription tiers and owner subscriptions
          </p>
        </div>
        <Button 
          variant="outline" 
          className="border-slate-600 text-slate-300"
          onClick={fetchData}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-green-400" />
            </div>
          </div>
          <p className="font-heading text-2xl font-bold text-white">{stats?.total_active || 0}</p>
          <p className="text-sm text-slate-400">Active Subscriptions</p>
        </div>
        {['standard', 'featured', 'concierge'].map((tier) => {
          const Icon = TIER_ICONS[tier];
          return (
            <div key={tier} className="bg-slate-800 rounded-xl border border-slate-700 p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 ${TIER_COLORS[tier]}/20 rounded-lg flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${tier === 'featured' ? 'text-amber-400' : tier === 'concierge' ? 'text-trust-blue' : 'text-slate-400'}`} />
                </div>
              </div>
              <p className="font-heading text-2xl font-bold text-white">{stats?.by_tier?.[tier] || 0}</p>
              <p className="text-sm text-slate-400 capitalize">{tier}</p>
            </div>
          );
        })}
      </div>

      {/* Tier Configuration */}
      <div className="mb-8">
        <h2 className="font-heading text-xl font-semibold text-white mb-4">
          Tier Configuration
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {tiers.map((tier) => {
            const Icon = TIER_ICONS[tier.tier] || Home;
            const isEditing = editingTier === tier.tier;
            
            return (
              <div 
                key={tier.tier}
                className={`bg-slate-800 rounded-xl border p-6 ${
                  tier.enabled !== false ? 'border-slate-700' : 'border-red-500/30 opacity-75'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 ${TIER_COLORS[tier.tier]} rounded-xl flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{tier.name}</h3>
                      <p className="text-xs text-slate-400 capitalize">{tier.tier}</p>
                    </div>
                  </div>
                  <Switch
                    checked={tier.enabled !== false}
                    onCheckedChange={() => handleToggleTier(tier.tier, tier.enabled !== false)}
                    data-testid={`toggle-${tier.tier}`}
                  />
                </div>

                {/* Price */}
                <div className="mb-4">
                  <label className="text-sm text-slate-400 mb-1 block">Monthly Price</label>
                  {isEditing ? (
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          type="number"
                          value={editPrice}
                          onChange={(e) => setEditPrice(e.target.value)}
                          className="pl-8 bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleSavePrice(tier.tier)}
                        disabled={savingPrice}
                      >
                        {savingPrice ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingTier(null)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-white">${tier.price}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-slate-400 hover:text-white"
                        onClick={() => handleEditPrice(tier)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Features */}
                <div className="space-y-2">
                  {tier.features?.slice(0, 3).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-slate-400">
                      <Check className="w-4 h-4 text-green-500" />
                      <span className="line-clamp-1">{feature}</span>
                    </div>
                  ))}
                  {tier.features?.length > 3 && (
                    <p className="text-xs text-slate-500">+{tier.features.length - 3} more features</p>
                  )}
                </div>

                {/* Badges */}
                <div className="mt-4 pt-4 border-t border-slate-700 flex gap-2">
                  {tier.featured_badge && (
                    <Badge className="bg-amber-500/20 text-amber-400">Featured Badge</Badge>
                  )}
                  {tier.concierge && (
                    <Badge className="bg-blue-500/20 text-blue-400">Concierge</Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Owner Subscriptions List */}
      <div>
        <h2 className="font-heading text-xl font-semibold text-white mb-4">
          Owner Subscriptions
        </h2>
        
        {subscriptions.length === 0 ? (
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-12 text-center">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No subscriptions yet</p>
          </div>
        ) : (
          <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left text-slate-400 font-medium px-6 py-4">Owner</th>
                  <th className="text-left text-slate-400 font-medium px-6 py-4">Tier</th>
                  <th className="text-left text-slate-400 font-medium px-6 py-4">Status</th>
                  <th className="text-left text-slate-400 font-medium px-6 py-4">Renewal</th>
                  <th className="text-left text-slate-400 font-medium px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {subscriptions.map((sub) => {
                  const Icon = TIER_ICONS[sub.tier] || Home;
                  return (
                    <tr key={sub.subscription_id} className="hover:bg-slate-700/30">
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-white font-medium">{sub.user_name || 'N/A'}</p>
                          <p className="text-sm text-slate-400">{sub.user_email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 ${TIER_COLORS[sub.tier]} rounded-lg flex items-center justify-center`}>
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-white capitalize">{sub.tier}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={
                          sub.status === 'active' ? 'bg-green-500/20 text-green-400' :
                          sub.status === 'past_due' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-red-500/20 text-red-400'
                        }>
                          {sub.status}
                        </Badge>
                        {sub.cancel_at_period_end && (
                          <Badge className="ml-2 bg-amber-500/20 text-amber-400">
                            Canceling
                          </Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 text-slate-400">
                        {sub.current_period_end 
                          ? format(new Date(sub.current_period_end), 'MMM d, yyyy')
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <Select
                          value={sub.tier}
                          onValueChange={(value) => handleChangeUserTier(sub.user_id, value)}
                          disabled={changingUser === sub.user_id}
                        >
                          <SelectTrigger className="w-32 bg-slate-700 border-slate-600 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="standard">Standard</SelectItem>
                            <SelectItem value="featured">Featured</SelectItem>
                            <SelectItem value="concierge">Concierge</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSubscriptionsPage;
